import { detectTimezone, hasFixedTimezone, PLATFORM_TIMEZONE } from "./timezone";

/**
 * The timezone every date on screen is rendered in.
 *
 * Pharmacy accounts: always Asia/Baghdad (PLATFORM_TIMEZONE), fixed.
 *
 * Doctors: the zone of the device they are using, so online consultations, slots and dates follow
 * the device's own clock. The backend returns appointment dates/times already converted (online on
 * this zone, sent as the X-Timezone header; home visits on Iraq time), so this governs the dates the
 * app formats itself.
 */
export const getDisplayTimezone = () => {
  if (hasFixedTimezone()) return PLATFORM_TIMEZONE;
  return detectTimezone();
};

/** Home visits run on Iraq time: every date and time a doctor enters or sees for one is Asia/Baghdad. */
export const HOME_VISIT_TIMEZONE = PLATFORM_TIMEZONE;

/**
 * "Now" on a given clock: today's date there (`YYYY-MM-DD`) and minutes since midnight.
 *
 * Used for home-visit "today" and "already past" checks, which must run on Iraq time whatever the
 * device's clock says.
 */
export const nowInZone = (timeZone) => {
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).formatToParts(new Date());
    const get = (type) => parts.find((p) => p.type === type)?.value || "";
    return {
      date: `${get("year")}-${get("month")}-${get("day")}`,
      minutes: Number(get("hour")) * 60 + Number(get("minute")),
    };
  } catch {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return {
      date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
      minutes: d.getHours() * 60 + d.getMinutes(),
    };
  }
};

/**
 * A `YYYY-MM-DD` calendar day as a local Date at midnight, for date pickers (`selected`, `minDate`).
 * The picker shows the day by its local Y-M-D, so this displays exactly that calendar day.
 */
export const ymdToLocalDate = (ymd) => {
  const [y, m, d] = String(ymd || "").split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

/** Shown when a value is absent or unparseable. */
const EMPTY = "—";

const toDate = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * `en-US` because that is the locale the existing call sites already used.
 *
 * Deliberate restraint: this is about WHICH CLOCK a time is shown on, not about restyling dates.
 * Changing the locale as well would quietly alter every screen — a change nobody asked for, mixed
 * into one they did.
 */
const LOCALE = "en-US";

/**
 * The one place `timeZone` is applied.
 *
 * Every formatter below goes through here, so there is a single answer to "which clock is this?".
 * `Intl` handles daylight saving and historical offsets itself, which is why none of this needs date
 * maths of its own — the entire fix is passing the zone it was never given before.
 */
const formatIn = (value, locale, options) => {
  const date = toDate(value);
  if (!date) return EMPTY;
  try {
    return new Intl.DateTimeFormat(locale || LOCALE, {
      ...options,
      timeZone: getDisplayTimezone(),
    }).format(date);
  } catch {
    // An environment without timezone data must not blank the screen. Fall back to the browser's zone.
    try {
      return new Intl.DateTimeFormat(locale || LOCALE, options).format(date);
    } catch {
      return EMPTY;
    }
  }
};

const format = (value, options) => formatIn(value, LOCALE, options);

/**
 * Same as `formatCustom` but keeping an explicitly chosen locale.
 *
 * Needed because a handful of screens deliberately render `en-GB` (29/09/2026 rather than
 * 9/29/2026). Forcing those to the default locale would change the day/month order — a silent
 * restyle on top of the timezone fix, and the kind that is easy to misread rather than merely notice.
 */
export const formatCustomInLocale = (value, locale, options) =>
  formatIn(value, locale, options);

/** `Jul 15, 2026` */
export const formatDate = (value) =>
  format(value, { year: "numeric", month: "short", day: "numeric" });

/** `July 15, 2026` — long month, for headings and detail views. */
export const formatDateLong = (value) =>
  format(value, { year: "numeric", month: "long", day: "numeric" });

/** `Jul 15, 2026, 05:30 PM` */
export const formatDateTime = (value) =>
  format(value, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

/** `Jul 15, 2026, 05:30:09 PM` — where the exact second matters. */
export const formatDateTimeWithSeconds = (value) =>
  format(value, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

/** `05:30 PM` */
export const formatTime = (value) =>
  format(value, { hour: "2-digit", minute: "2-digit" });

/** `Jul 15` — compact labels. */
export const formatDayMonth = (value) =>
  format(value, { month: "short", day: "numeric" });

/*
 * The three below exist to replace a BARE `toLocale*String()` without changing how it looks.
 *
 * A bare call renders numerically ("9/29/2026", "4:48:26 PM"). Routing those through the nicer named
 * formatters above would silently restyle dates all over the app, so these reproduce the default
 * shapes exactly and change only the clock they are rendered on.
 */

/** Matches a bare `toLocaleDateString()` — e.g. `9/29/2026`. */
export const formatDateNumeric = (value) =>
  format(value, { year: "numeric", month: "numeric", day: "numeric" });

/** Matches a bare `toLocaleTimeString()` — e.g. `4:48:26 PM`. */
export const formatTimeNumeric = (value) =>
  format(value, { hour: "numeric", minute: "2-digit", second: "2-digit" });

/** Matches a bare `toLocaleString()` — e.g. `9/29/2026, 4:48:26 PM`. */
export const formatDateTimeNumeric = (value) =>
  format(value, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });

/** `Wednesday` */
export const formatWeekday = (value) => format(value, { weekday: "long" });

/**
 * Escape hatch for a one-off shape, still in the right zone.
 *
 * Prefer the named formatters: consistent dates across the app is most of the value here, and an
 * options object at the call site is how that drifts.
 */
export const formatCustom = (value, options) => format(value, options);

/**
 * A calendar day (`YYYY-MM-DD`) formatted as that same day, with no timezone shift.
 *
 * For appointment dates the backend has ALREADY converted to the right zone (online: this device;
 * home visit: Iraq). `new Date("YYYY-MM-DD")` is UTC midnight, so formatting it in a zone west of
 * UTC would show the previous day. Anything else falls back to formatCustom.
 */
export const formatCalendarDay = (value, options, locale) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? ""));
  if (!m) return formatIn(value, locale, options);
  try {
    return new Intl.DateTimeFormat(locale || LOCALE, {
      ...options,
      timeZone: "UTC",
    }).format(
      new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))),
    );
  } catch {
    return `${m[1]}-${m[2]}-${m[3]}`;
  }
};

/**
 * `2026-07-15` in the DISPLAY zone — the calendar day the user is looking at.
 *
 * For the specific case of turning an instant into a day key for display or comparison. NOT for
 * building API payloads from a user-picked date: a date chosen in a picker is already a calendar day
 * with no instant behind it, and pushing it through a timezone would shift which day the server is
 * asked about. Those call sites deliberately still use date-fns `format(d, "yyyy-MM-dd")`.
 */
export const formatIsoDateInDisplayZone = (value) => {
  const date = toDate(value);
  if (!date) return "";
  try {
    // en-CA renders ISO-ordered yyyy-mm-dd, which avoids assembling it from parts by hand.
    return new Intl.DateTimeFormat("en-CA", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      timeZone: getDisplayTimezone(),
    }).format(date);
  } catch {
    return "";
  }
};

/** The zone itself, for a label like "times shown in Asia/Baghdad". */
export const displayTimezoneLabel = () => getDisplayTimezone();

/**
 * A zone's UTC offset at `date` (default now) as "GMT+3", "GMT+5:30", "GMT-4" or "GMT".
 *
 * Worked out from the zone's wall clock rather than Intl's `timeZoneName: "shortOffset"`, which
 * older browsers do not support and which some render as an abbreviation instead of an offset.
 * Returns "" when the zone is not usable, so the caller can simply omit it.
 */
export const timezoneOffsetLabel = (timeZone, date = new Date()) => {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).formatToParts(date);
    const get = (type) => Number(parts.find((p) => p.type === type)?.value);
    const wallClockAsUtc = Date.UTC(
      get("year"),
      get("month") - 1,
      get("day"),
      get("hour") % 24,
      get("minute"),
      get("second"),
    );
    // Compare at whole-second precision: the wall clock above has no milliseconds.
    const instant = Math.floor(date.getTime() / 1000) * 1000;
    const offsetMinutes = Math.round((wallClockAsUtc - instant) / 60000);
    if (!Number.isFinite(offsetMinutes)) return "";
    if (offsetMinutes === 0) return "GMT";
    const sign = offsetMinutes > 0 ? "+" : "-";
    const abs = Math.abs(offsetMinutes);
    const hours = Math.floor(abs / 60);
    const minutes = abs % 60;
    return `GMT${sign}${hours}${minutes ? `:${String(minutes).padStart(2, "0")}` : ""}`;
  } catch {
    return "";
  }
};
